import { Injectable } from '@angular/core';
import { BaseService } from '@eview/core/base/base-service';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Tags, Tag, ListTagsOptions } from './tag';

export interface BulkAssignParentResponse {
  parent_id: number | null;
  ids: number[];
  updated: number;
}

export interface TagImportCounts {
  total: number;
  create: number;
  skip_duplicate: number;
  invalid: number;
  committed: number;
}

export interface TagImportStatus {
  id: number;
  status: string;
  status_details?: string | null;
  entity_type?: string;
  type?: string | null;
  mode?: 'root' | 'child';
  allowed_parent_types?: string[];
  counts: TagImportCounts;
  total_rows?: number | null;
  error?: string | null;
  can_commit?: boolean;
  success?: boolean;
  message?: string | string[];
}

export interface TagImportRow {
  id: number;
  row_number: number;
  parent_id: number | null;
  parent_name: string | null;
  tag: string | null;
  planned_slug: string | null;
  status: string;
  errors: string[] | null;
}

export interface TagImportRowsPage {
  id: number;
  page: number;
  limit: number;
  total: number;
  results: TagImportRow[];
}

@Injectable({
  providedIn: 'root'
})
export class TagService extends BaseService {
  readonly uris = {
    create: 'tags',
    list: 'tags',
    read: 'tags/:id',
    update: 'tags/:id',
    delete: 'tags/:id',
    bulkParent: 'tags/bulk/parent',
    importPreview: 'tags/import/preview',
    importStatus: 'tags/import/:id',
    importRows: 'tags/import/:id/rows',
    importCommit: 'tags/import/:id/commit'
  };

  create(tag: Tag): Observable<Tag> {
    return this.http.post<Tag>(this.getUrl(this.uris.create), tag);
  }

  list(options: ListTagsOptions = {}): Observable<Tags> {
    const params = this.getParamsFromObject(this.cleanListOptions(options));
    return this.http
      .get<Tags>(this.getUrl(this.uris.list), params ? { params } : {})
      .pipe(
        catchError(err => {
          return this.cacheHandler(err, 'tags');
        })
      );
  }

  /** Drop empty filters so the API does not treat "undefined" as a value. */
  private cleanListOptions(
    options: ListTagsOptions
  ): Record<string, string | number> {
    const out: Record<string, string | number> = {};
    if (!options) {
      return out;
    }
    Object.keys(options).forEach(key => {
      const value = (options as Record<string, unknown>)[key];
      if (value === undefined || value === null || value === '') {
        return;
      }
      out[key] = value as string | number;
    });
    return out;
  }

  read(id: number): Observable<Tag> {
    return this.http.get<Tag>(
      this.getUrl(this.uris.read).replace(':id', id.toString())
    );
  }

  update(tag: Tag): Observable<Tag> {
    return this.http.put<Tag>(
      this.getUrl(this.uris.update).replace(':id', tag.id.toString()),
      tag
    );
  }

  /**
   * Assign or clear parent_id for many tags in one request.
   * parent_id = null detaches (makes roots).
   */
  bulkAssignParent(
    ids: number[],
    parentId: number | null
  ): Observable<BulkAssignParentResponse> {
    return this.http.put<BulkAssignParentResponse>(
      this.getUrl(this.uris.bulkParent),
      { ids, parent_id: parentId }
    );
  }

  delete(tag: { id: number }): Observable<Tag> {
    return this.http.delete<Tag>(
      this.getUrl(this.uris.delete).replace(':id', tag.id.toString())
    );
  }

  importPreview(
    file: File,
    type: string,
    allowedParentTypes: string[],
    mode: 'root' | 'child' = 'child'
  ): Observable<TagImportStatus> {
    const body = new FormData();
    body.append('csv', file, file.name);
    body.append('type', type);
    body.append('mode', mode);
    body.append('allowed_parent_types', JSON.stringify(allowedParentTypes || []));
    return this.http.post<TagImportStatus>(
      this.getUrl(this.uris.importPreview),
      body
    );
  }

  importStatus(jobId: number): Observable<TagImportStatus> {
    return this.http.get<TagImportStatus>(
      this.getUrl(this.uris.importStatus).replace(':id', String(jobId))
    );
  }

  importRows(
    jobId: number,
    page = 1,
    limit = 50,
    status?: string
  ): Observable<TagImportRowsPage> {
    const params: Record<string, string> = {
      page: String(page),
      limit: String(limit)
    };
    if (status) {
      params.status = status;
    }
    return this.http.get<TagImportRowsPage>(
      this.getUrl(this.uris.importRows).replace(':id', String(jobId)),
      { params }
    );
  }

  importCommit(jobId: number): Observable<TagImportStatus> {
    return this.http.post<TagImportStatus>(
      this.getUrl(this.uris.importCommit).replace(':id', String(jobId)),
      {}
    );
  }
}
